import i18next from 'i18next';
import { List } from 'immutable';
import * as React from 'react';
import { StandardLocalWidget, any, fromJSX, loadedAsyncState } from "widgets-for-react";
import { changePasswordWidget } from "../../forms/changePassword/changePasswordWidget";
import { invoiceSelectorWidget } from '../../forms/invoiceSelector/invoiceSelectorWidget';
import { personalInfoWidget } from '../../forms/personalInfo/personalInfoWidget';
import { RootLayout } from "../../site/root/rootLayout";
import { authOptions } from '../../site/root/rootWidget';
import { routeUpdaters } from "../../site/root/routes/routesState";
import { formatDate } from "../../utils/date";
import { SubscriptionLayout } from "../subscriptionLayout";
import { SubscriptionState, subscriptionUpdaters } from "../subscriptionState";
import { formatSubscriptionPrice } from '../subscriptionUtils';
import { ManageSubscription } from './accountDetailsPageLayout';

// The new widget that works with Stripe subscriptions
export const accountDetailsPageWidget: StandardLocalWidget<SubscriptionState> = currentState => {
  const canManageSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus === "ValidSubscription"
  const isTrial = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus === "ValidTrial"
  const willBeStopped = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "StillValidTerminatedSubscription"
  const studentSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "ValidStudentSubscription"
  const studentSubscriptionWithPausedSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "ValidStudentSubscriptionWithPausedSubscription"
  const isStudent = studentSubscription || studentSubscriptionWithPausedSubscription

  const isStoppedOrNone = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus === "NoSubscription"

  return any( [fromJSX(setState =>
    currentState.userSubscriptions.kind != "loaded"
    // This should never happen because the subscriptionWidget already checks for this
    ? <RootLayout.Loader showText hasWrapper />
    : 
    <SubscriptionLayout.Wrapper
        id="test-accountManagement"
        breadcrumbs={List([{ url: routeUpdaters(i18next.language).dashboard.url, label: 'dashboard' }])}
        >
        <SubscriptionLayout.Section title={i18next.t('subscription:YourAccount')}>
            {/* ----- Account type */}
            {!currentState.userSubscriptions.value.StripeSubscription
            ? <SubscriptionLayout.AccountType
                kind='trial' 
                intro={<p>No Stripe Subscription</p>} // Should never happen, but have to satisfy the TS compiler
                />
            : willBeStopped
            ? <SubscriptionLayout.AccountType
                kind="will-be-stopped"
                intro={<p>{i18next.t("subscription:WillBeStoppedSubscriptionIntro", {
                terminationDate: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.value.StripeSubscription.CancelAt)
                })}</p>}
            />
            : isStoppedOrNone
                ? <SubscriptionLayout.AccountType
                kind="stopped-or-none"
                intro={<p>{i18next.t("subscription:StoppedSubscriptionIntro")}</p>}
                />
                : isTrial
                ? <SubscriptionLayout.AccountType
                    kind="trial"
                    intro={
                    <p>
                        {i18next.t("subscription:TrialSubscriptionIntro", { daysLeft: Math.ceil(currentState.userSubscriptions.value.StripeSubscription.TrialDaysLeft) })}
                    </p>
                    }
                />
                : <SubscriptionLayout.AccountType
                    kind="pro"
                    intro={
                    <p>
                        {i18next.t("subscription:ProSubscriptionIntro", {
                        paymentFrequency: i18next.t(`subscription:${currentState.userSubscriptions.value.StripeSubscription?.PaymentFrequency}`),
                        amount: currentState.userSubscriptions.value.StripeSubscription?.Amount ? formatSubscriptionPrice(currentState.userSubscriptions.value.StripeSubscription.Amount, i18next.language) : 0,
                        currency: "€",
                        nextPayment: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.value.StripeSubscription.NextPaymentDate || Date.now())
                        })}
                    </p>
                    }
                />
            }

            {/* ----- Open invoices */}
            {currentState.userSubscriptions.value.OpenPayments.length > 0
            ? <SubscriptionLayout.OpenInvoice options={authOptions}>
                {currentState.userSubscriptions.value.OpenPayments.map(openPayment =>
                <SubscriptionLayout.OpenInvoiceItem
                    openPayment={openPayment}
                    onPay={() => setState(subscriptionUpdaters.startPayOpenPaymentInputFlow(openPayment))}
                />
                )}
            </SubscriptionLayout.OpenInvoice>
            : <></>}
        </SubscriptionLayout.Section>


        {/* ----- Settings */}
        <SubscriptionLayout.Section title={i18next.t('subscription:Settings')} suppressHotjar={true}>
            {/* E-mail */}
            <SubscriptionLayout.Email email={currentState.userSubscriptions.value.User?.Email} />

            {/* Change password */}
            <SubscriptionLayout.ChangePassword
            onSubmit={currentState.editing?.kind == "password"
                ? () => setState(subscriptionUpdaters.invokeChangePasswordApi)
                : undefined}
            onClick={currentState.editing?.kind != "password"
                ? () => setState(subscriptionUpdaters.startChangePasswordSubFlow())
                : undefined}
            >
            {currentState.editing?.kind == "password"
                ? <>
                <p>{i18next.t("auth:changePwPrompt")}</p>
                {changePasswordWidget(currentState.editing.changePasswordState)
                    .map(subscriptionUpdaters.changePasswordState)
                    .run(setState)}
                <SubscriptionLayout.ChangePasswordButtons
                    cancel={() => setState(subscriptionUpdaters.cancelSubFlow())}
                    save={() => setState(subscriptionUpdaters.invokeChangePasswordApi)}
                    changePasswordDisabled={!currentState.editing.changePasswordState.isValid()}
                />
                </>
                : <SubscriptionLayout.ChangePasswordPlaceholder
                edit={() => setState(subscriptionUpdaters.startChangePasswordSubFlow())}
                />
            }
            </SubscriptionLayout.ChangePassword>

            {/* Personal info */}
            <SubscriptionLayout.PersonalInfo
                onSubmit={currentState.editing?.kind == "personal info"
                    ? () => setState(subscriptionUpdaters.invokeChangePersonalInfoApi)
                    : undefined}
                onClick={currentState.editing?.kind != "personal info"
                    ? () => setState(subscriptionUpdaters.startChangePersonalInfoSubFlow())
                    : undefined}
            >
                {currentState.editing?.kind == "personal info"
                    ? <>
                    {currentState.lookupData.kind == "loaded" ?
                        personalInfoWidget(currentState.lookupData.value.Languages, currentState.lookupData.value.Countries, currentState.lookupData.value.Jobs, false, currentState.userSubscriptions.value.User.Country !== null, false, isStudent, true)(currentState.editing.personalInfoState)
                        .map(subscriptionUpdaters.changePersonalInfoPersonalInfoState)
                        .run(setState)
                        : <RootLayout.Loader showText hasWrapper />}

                    <SubscriptionLayout.PersonalInfoButtonsWithRouter
                        cancel={() => setState(subscriptionUpdaters.cancelSubFlow())}
                        save={() => {
                            const editing = currentState.editing
                            if (editing?.kind == "personal info" && currentState.lookupData.kind == "loaded") {
                                setState(
                                    subscriptionUpdaters.checkVATState(() =>loadedAsyncState(true))
                                )
                            }
                            if (currentState.editing?.kind == "personal info" && currentState.editing.personalInfoState.languageState.input.Code && i18next.language !== currentState.editing.personalInfoState.languageState.input.Code) {
                                i18next.changeLanguage(currentState.editing.personalInfoState.languageState.input.Code)
                            }
                        }}
                        onSaveGoTo={
                            currentState.editing?.kind == "personal info" && currentState.editing.personalInfoState.languageState.input.Code && i18next.language !== currentState.editing.personalInfoState.languageState.input.Code
                                ? routeUpdaters(currentState.editing.personalInfoState.languageState.input.Code).accountDetails.url
                                : undefined
                        }
                        saveDisabled={!currentState.editing.personalInfoState.isValid(true) || (currentState.editing.invoiceToCompany && !currentState.editing.companyState.isValid())}
                    />
                    </>
                    : currentState.lookupData.kind == "loaded" ?
                    <SubscriptionLayout.PersonalInfoPlaceholder
                        userSubscriptions={currentState.userSubscriptions.value}
                        lookupData={currentState.lookupData.value}
                        edit={() => setState(subscriptionUpdaters.startChangePersonalInfoSubFlow())}
                        isStudent={isStudent}
                    />
                    : <RootLayout.Loader showText hasWrapper />
                }
            </SubscriptionLayout.PersonalInfo>
        </SubscriptionLayout.Section>

        {/* ----- Change account */}
        <SubscriptionLayout.Section title={i18next.t('subscription:ChangeYourAccount')}>
            {/* Manage subscription */}
            {!canManageSubscription ? <></> :
                <ManageSubscription 
                    intro={i18next.t('subscription:ManageSubscriptionIntro')}
                    onClick={() => setState(subscriptionUpdaters.startSubscriptionPortalSession())}
                />
            }

            {/* Delete account */}
            <SubscriptionLayout.DeleteAccount />
        </SubscriptionLayout.Section>

        {/* Invoices */}
        {(isTrial || isStudent) 
            ? <></> 
            : invoiceSelectorWidget(currentState.invoiceSelectorState).map(subscriptionUpdaters.invoiceSelectorState).run(setState)}
    </SubscriptionLayout.Wrapper>
  )
])
}